import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { format } from 'date-fns';
import isEmpty from 'lodash.isempty';
import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import Alert from '../components/Alert';
import NavButton from '../components/NavButton';
import NewApplicationDropdown from '../components/NewApplicationDropdown';
import { useUserStore } from '../components/userStore';
import constants from '../constants';
import InvoiceModal from './ApplicationModules/components/InvoiceModal';

const Dashboard = () => {
	const { user } = useUserStore();
	let { dashType, page = 1 } = useParams();
	const [limit /* setLimit */] = useState(11);
	const [isInvoiceModalOpen, setInvoiceModalOpen] = useState(false);
	const [requestId, setRequestId] = useState(null);

	const isExecutingAgency = ['executing', 'healthplan', 'clinical'].includes(
		user?.coord_agency?.agency_type,
	);
	if (!isExecutingAgency) {
		return <Navigate to="/worklist" replace />;
	}

	const dashboardQuery = useQuery({
		queryKey: ['dashboard', user.coord_agency_id, dashType, page, limit],
		queryFn: async () => {
			let parsedPage = parseInt(page, 10);
			if (isNaN(parsedPage)) {
				parsedPage = 1;
			}
			let list = 'open-referrals';
			if ('wait-list' === dashType) {
				list = 'wait-list';
			} else if ('fulfillment' === dashType) {
				list = 'in-fulfillment';
			}
			const { data } = await axios.get(
				`/coord_agencies/${user.coord_agency_id}/${list}`,
				{
					params: { options: { skip: (parsedPage - 1) * (limit - 1), limit } },
				},
			);
			return data;
		},
	});

	const fulfillmentCount = useQuery({
		queryKey: ['fulfillmentCount', user.coord_agency_id],
		queryFn: async () => {
			const requestIds = dashboardQuery?.data?.map((req) => req.id) || [];
			const { data } = await axios.get(
				`/coord_agencies/${user.coord_agency_id}/get-line-item-counts`,
				{ params: { requestIds } },
			);
			return data;
		},
		enabled: dashType === 'fulfillment' && dashboardQuery.isSuccess,
	});

	page = parseInt(page, 10);

	return (
		<>
			<div className="flex justify-end">
				<NewApplicationDropdown />
			</div>
			<div className="px-4 py-4 mt-4 bg-white rounded-lg">
				{dashboardQuery.isError && (
					<Alert message="There was a problem loading data!" />
				)}
				<table className="table table-zebra table-sm">
					<thead>
						<tr>
							<th>Created Date</th>
							<th>Client Name</th>
							<th>Referring Org</th>
							<th>Service</th>
							<th>Status</th>
						</tr>
					</thead>
					<tbody>
						{dashboardQuery.isFetching && (
							<tr>
								<td colSpan={6}>Loading...</td>
							</tr>
						)}
						{dashboardQuery.isFetched &&
							!dashboardQuery.isError &&
							dashboardQuery?.data?.length === 0 && (
								<tr>
									<td colSpan={6}>No service requests found.</td>
								</tr>
							)}
						{dashboardQuery.isFetched &&
							!dashboardQuery.isError &&
							dashboardQuery?.data?.length > 0 &&
							dashboardQuery?.data?.slice(0, 10).map((serviceRequest) => (
								<tr key={`${serviceRequest.id}`}>
									<td>{format(new Date(serviceRequest.request_dttm), 'Pp')}</td>
									<td>
										{serviceRequest.client.name_first}{' '}
										{serviceRequest.client.name_last}
									</td>
									<td>
										{serviceRequest?.coord_agency
											? serviceRequest.coord_agency?.agency_name
											: 'Self Referred'}
									</td>
									<td>
										{serviceRequest.taxonomy_detail ||
											serviceRequest?.external_211_service?.data?.name}
									</td>
									<td>
										{!isEmpty(serviceRequest.request_status) &&
											constants.status[serviceRequest.request_status]}
									</td>
									<td className="text-right">
										{dashType === 'fulfillment' &&
											fulfillmentCount?.data?.find(
												({ request_id }) => request_id === serviceRequest.id,
											) && (
												<button
													className="mr-2 btn btn-secondary btn-sm"
													onClick={() => {
														setRequestId(serviceRequest.id);
														setInvoiceModalOpen(true);
													}}
												>
													{
														fulfillmentCount?.data?.find(
															({ request_id }) =>
																request_id === serviceRequest.id,
														)?.count
													}{' '}
													Open
												</button>
											)}
										<NavButton
											to={`/worklist/application/${serviceRequest.id}`}
											title="View"
										/>
									</td>
								</tr>
							))}
					</tbody>
				</table>
				<div className="flex justify-end py-4 mt-4">
					<div className="grid grid-cols-2 w-72 join">
						<Link
							className="join-item btn btn-outline btn-secondary"
							disabled={page == 1}
							to={`/${dashType}/${page - 1}`}
						>
							Previous page
						</Link>
						<Link
							className="join-item btn btn-outline btn-secondary"
							disabled={dashboardQuery?.data?.length <= limit - 1}
							to={`/${dashType}/${page + 1}`}
						>
							Next page
						</Link>
					</div>
				</div>
				{dashType === 'fulfillment' && (
					<InvoiceModal
						isOpen={isInvoiceModalOpen}
						requestId={requestId}
						onClose={() => setInvoiceModalOpen(false)}
					/>
				)}
			</div>
		</>
	);
};

export default Dashboard;
